import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Select from 'react-select';
import SalesLayout from '../../components/SalesLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';
import { dedupeCourseOptions } from '../../utils/courseOptions';
import {
    canShareStudentForm,
    getStudentFormUrl,
    STUDENT_FORM_INQUIRY_TYPE,
} from '../../utils/studentForm';

const formatDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const statusStyles = (key) => {
    switch (String(key ?? '').toLowerCase()) {
    case 'registered':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'in followup':
    case 'plan to register':
        return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
};

const toWhatsAppNumber = (value) => {
    const digits = String(value ?? '').replace(/\D+/g, '');
    return digits.startsWith('0') ? `94${digits.slice(1)}` : digits;
};

const newestFirst = (a, b) => {
    const ta = new Date(a.createdDate || 0).getTime() || 0;
    const tb = new Date(b.createdDate || 0).getTime() || 0;
    if (tb !== ta) return tb - ta;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
};

function ShareCard({ baseUrl, courseOptions }) {
    const [copied, setCopied] = useState(false);
    const [course, setCourse] = useState(null);
    // The course id is never put in the link -- only its encrypted token.
    const url = course?.shareToken ? `${baseUrl}?c=${course.shareToken}` : baseUrl;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            const input = document.createElement('input');
            input.value = url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <section className="rounded-2xl border border-indigo-200 bg-white p-4 shadow-sm sm:p-5">
            <h2 className="text-sm font-semibold text-slate-900 sm:text-base">Student inquiry form link</h2>
            <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                Share this link with students. They can fill it in without logging in.
            </p>

            <div className="mt-3">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Share for a specific course (optional)
                </label>
                <Select
                    className="mt-1 text-sm"
                    classNamePrefix="rs"
                    options={courseOptions}
                    value={course}
                    onChange={(option) => setCourse(option)}
                    isClearable
                    placeholder="All courses – student picks the course"
                    menuPlacement="auto"
                    getOptionLabel={(o) => o.shortName ?? o.label}
                    formatOptionLabel={(o) => (
                        <div className="flex flex-col">
                            <span className="text-xs font-semibold text-slate-900">{o.shortName ?? o.label}</span>
                            {o.fullName ? <span className="text-[11px] text-slate-500">{o.fullName}</span> : null}
                        </div>
                    )}
                    filterOption={(candidate, input) => {
                        const q = input.trim().toLowerCase();
                        if (!q) return true;
                        return [candidate.data.shortName, candidate.data.fullName, candidate.data.label]
                            .filter(Boolean)
                            .some((v) => String(v).toLowerCase().includes(q));
                    }}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                    {course
                        ? 'This link opens the form with the course already selected.'
                        : 'Without a course, students choose the course themselves.'}
                </p>
            </div>
            <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 block truncate rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-indigo-700 ring-1 ring-inset ring-slate-200"
            >
                {url}
            </a>
            {course ? (
                <p className="mt-2 text-xs font-medium text-indigo-700">
                    Course: {course.fullName ?? course.shortName ?? course.label}
                </p>
            ) : null}
            <div className="mt-3 grid grid-cols-3 gap-2 sm:flex">
                <button
                    type="button"
                    onClick={copy}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
                >
                    {copied ? 'Copied ✓' : 'Copy link'}
                </button>
                <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${course ? `Please fill in this form for ${course.shortName ?? course.label}` : 'Please fill in this form'}: ${url}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-center text-xs font-semibold text-emerald-700 shadow-sm hover:bg-emerald-100"
                >
                    WhatsApp
                </a>
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-center text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
                >
                    Open form
                </a>
            </div>
        </section>
    );
}

export default function StudentForm({ pageTitle, section }) {
    const allowed = canShareStudentForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [rows, setRows] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!allowed) return undefined;
        let cancelled = false;
        axiosInstance
            .get('/admin/sales-inquiries')
            .then((res) => {
                if (cancelled) return;
                setCourseOptions(dedupeCourseOptions(res?.data?.courseOptions ?? [], (o) => o.shortName ?? o.fullName ?? o.label));
                const all = res?.data?.inquiries ?? [];
                setRows(all.filter((row) => row.inquiryType === STUDENT_FORM_INQUIRY_TYPE).sort(newestFirst));
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load form submissions.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [allowed]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        const qDigits = q.replace(/\D+/g, '');
        return rows.filter((row) => {
            if ([row.studentName, row.nic, row.course].some((v) => (v ?? '').toLowerCase().includes(q))) return true;
            return qDigits.length >= 3
                && [row.contactNumber1, row.contact].some((c) => String(c ?? '').replace(/\D+/g, '').includes(qDigits));
        });
    }, [rows, search]);

    if (!allowed) return <Navigate to="/sales/dashboard" replace />;

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            {loading ? (
                <PageLoader label="Loading form submissions…" />
            ) : (
                <div className="space-y-4">
                    <ShareCard baseUrl={getStudentFormUrl()} courseOptions={courseOptions} />

                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-slate-900 sm:text-base">
                                    Form submissions ({rows.length})
                                </h2>
                                <p className="text-xs text-slate-500">Newest first. Each one is also in your Inquiry list.</p>
                            </div>
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name, NIC, mobile, course…"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 sm:w-72"
                            />
                        </div>

                        {error ? (
                            <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>
                        ) : null}

                        {visible.length === 0 && !error ? (
                            <p className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                                {rows.length === 0
                                    ? 'No one has filled the form yet. Share the link above to get started.'
                                    : 'No submissions match your search.'}
                            </p>
                        ) : (
                            <ul className="mt-4 space-y-3">
                                {visible.map((row) => (
                                    <li key={row.id} className="rounded-xl border border-slate-200 p-3 shadow-sm sm:p-4">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-semibold text-slate-900">{row.studentName ?? '—'}</p>
                                                <p className="text-[11px] text-slate-500">{formatDateTime(row.createdDate)}</p>
                                            </div>
                                            <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${statusStyles(row.statusKey)}`}>
                                                {row.statusLabel}
                                            </span>
                                        </div>

                                        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                            <div className="col-span-2">
                                                <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Course</dt>
                                                <dd className="text-slate-800">{row.course ?? '—'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Mobile</dt>
                                                <dd className="text-slate-800">{row.contact ?? '—'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">NIC</dt>
                                                <dd className="break-all text-slate-800">{row.nic ?? '—'}</dd>
                                            </div>
                                            {[
                                                ['WhatsApp', row.contactNumber2],
                                                ['Email', row.email],
                                                ['City', row.city],
                                                ['Education', row.educationLevel],
                                                ['Date of birth', row.dateOfBirth ? String(row.dateOfBirth).slice(0, 10) : null],
                                                ['Gender', row.gender],
                                                ['Occupation', row.occupation],
                                            ].filter(([, value]) => value).map(([label, value]) => (
                                                <div key={label}>
                                                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                                                    <dd className="break-words text-slate-800">{value}</dd>
                                                </div>
                                            ))}
                                            {row.note ? (
                                                <div className="col-span-2">
                                                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Message</dt>
                                                    <dd className="whitespace-pre-line text-slate-700">{row.note}</dd>
                                                </div>
                                            ) : null}
                                        </dl>

                                        {row.contact ? (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                <a
                                                    href={`tel:${String(row.contact).replace(/[^\d+]/g, '')}`}
                                                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                                                >
                                                    Call
                                                </a>
                                                <a
                                                    href={`https://wa.me/${toWhatsAppNumber(row.contact)}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                                                >
                                                    WhatsApp
                                                </a>
                                                <a
                                                    href="/sales/inquiry"
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                                >
                                                    Open in Inquiries
                                                </a>
                                            </div>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </SalesLayout>
    );
}
